import { Button, Separator, TimeSlotCalendar } from '@longy/web-components'
import type { Prisma } from '@prisma-clinic/client'
import { CalendarCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useLocale } from '../../hooks/useLocale'
import { formatPrice } from '../../utils/formatPrice'

type SlotGroup = {
    serviceId: string
    serviceName: Prisma.JsonValue
    slots: {
        id: string
        startDate: Date | string
        endDate: Date | string
        price?: number | null
        purchasableItem?: { id: string } | null
    }[]
}

/**
 * The expert's free times, laid out the way mobile's ExpertCard lays them out.
 *
 * One collapsible calendar per service while the user is browsing everything the expert offers:
 * merging those would lose which service — and so which price — a slot belongs to. But once a
 * category has been picked the services behind it are the same kind of appointment, and the
 * question is only "when", so they collapse into a single calendar. Mobile draws the line with
 * `showGroupedCalendars = !categoryId && groupedSlots.length > 1`; this is that expression.
 */
export const SlotGroups = ({
    groups,
    selectedSlotId,
    isCategorySelected = false,
    onSelect,
}: {
    groups: SlotGroup[]
    selectedSlotId: string | undefined
    /** A chosen category merges the services it matches into one calendar. */
    isCategorySelected?: boolean
    onSelect: (slotId: string | undefined) => void
}) => {
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()

    // The first group opens so the page is not a wall of collapsed rows.
    const [expanded, setExpanded] = useState<string[]>(() =>
        groups[0] ? [groups[0].serviceId] : [],
    )

    const toggle = (serviceId: string) =>
        setExpanded((current) =>
            current.includes(serviceId)
                ? current.filter((id) => id !== serviceId)
                : [...current, serviceId],
        )

    const showGroupedCalendars = !isCategorySelected && groups.length > 1

    if (!showGroupedCalendars) {
        return (
            <MergedSlotCalendar
                groups={groups}
                selectedSlotId={selectedSlotId}
                onSelect={onSelect}
            />
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {groups.map((group) => {
                const isExpanded = expanded.includes(group.serviceId)
                const price = group.slots.find((slot) => slot.price != null)?.price
                const earliest = group.slots[0]

                return (
                    <div key={group.serviceId} className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-col">
                                <span className="font-semibold text-primary">
                                    {getLocalizedValue(group.serviceName)}
                                </span>
                                <span className="text-xs text-brand-gray">
                                    {t('frontend.booking.slotCount', {
                                        count: group.slots.length,
                                    })}
                                </span>
                                {price != null && (
                                    <span className="text-sm font-semibold text-brand-dark">
                                        {formatPrice(price)}
                                    </span>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-primary"
                                onClick={() => toggle(group.serviceId)}
                            >
                                {isExpanded
                                    ? t('frontend.booking.hideSlots')
                                    : t('frontend.booking.showSlots')}
                                <CalendarCheck className="ml-2 size-4" />
                            </Button>
                        </div>

                        {isExpanded && (
                            <TimeSlotCalendar
                                startDate={earliest ? new Date(earliest.startDate) : undefined}
                                availableTimeSlots={group.slots.map((slot) => ({
                                    id: slot.id,
                                    date: new Date(slot.startDate),
                                    endDate: new Date(slot.endDate),
                                    label: slot.price == null ? undefined : formatPrice(slot.price),
                                }))}
                                selectedSlotId={selectedSlotId}
                                onSelectedSlotChange={onSelect}
                            />
                        )}

                        <Separator />
                    </div>
                )
            })}
        </div>
    )
}

/**
 * Every group's times in one calendar: what a chosen category asks for, and what a single service
 * gets anyway because there is nothing to separate it from.
 *
 * The name and price are only shown when there is exactly one service behind the grid. Several
 * services can be priced differently, and one number over a merged calendar would be wrong for
 * some of the slots under it — the checkout prices the slot that was actually chosen.
 */
const MergedSlotCalendar = ({
    groups,
    selectedSlotId,
    onSelect,
}: {
    groups: SlotGroup[]
    selectedSlotId: string | undefined
    onSelect: (slotId: string | undefined) => void
}) => {
    const { t } = useTranslation()
    const { getLocalizedValue } = useLocale()
    const [isExpanded, setIsExpanded] = useState(true)

    const slots = groups
        .flatMap((group) => group.slots)
        .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())

    const singleGroup = groups.length === 1 ? groups[0] : null
    const price = singleGroup?.slots.find((slot) => slot.price != null)?.price
    const earliest = slots[0]

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col">
                    {singleGroup && (
                        <span className="font-semibold text-primary">
                            {getLocalizedValue(singleGroup.serviceName)}
                        </span>
                    )}
                    <span className="text-xs text-brand-gray">
                        {t('frontend.booking.slotCount', { count: slots.length })}
                    </span>
                    {price != null && (
                        <span className="text-sm font-semibold text-brand-dark">
                            {formatPrice(price)}
                        </span>
                    )}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary"
                    onClick={() => setIsExpanded((current) => !current)}
                >
                    {isExpanded ? t('frontend.booking.hideSlots') : t('frontend.booking.showSlots')}
                    <CalendarCheck className="ml-2 size-4" />
                </Button>
            </div>

            {isExpanded && (
                <TimeSlotCalendar
                    startDate={earliest ? new Date(earliest.startDate) : undefined}
                    availableTimeSlots={slots.map((slot) => ({
                        id: slot.id,
                        date: new Date(slot.startDate),
                        endDate: new Date(slot.endDate),
                        label: slot.price == null ? undefined : formatPrice(slot.price),
                    }))}
                    selectedSlotId={selectedSlotId}
                    onSelectedSlotChange={onSelect}
                />
            )}
        </div>
    )
}
